/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { forwardRef, useCallback, useMemo, useState } from 'react';
import { HiDocumentSearch, HiOutlineExclamation } from 'react-icons/hi';
import { TriangleDownIcon, TriangleUpIcon } from '@chakra-ui/icons';
import {
  Text,
  Flex,
  Button,
  Tr,
  Tbody,
  Th,
  TableContainer,
  Td,
  Table,
  Thead,
  Center,
  Icon,
  Box,
} from '@chakra-ui/react';
import { Card } from 'components';
import { TableSkele } from 'components/Skeletons/DataTable';
import { useCustomToast } from 'hooks';
import { downloadFile } from 'utils/functions';

interface CustomTableProps {
  data: any;
  labels: any;
  isMediation?: boolean;
  title: string;
  actionText: string;
  ariaText: string;
  total?: string;
  loading: boolean;
  error: boolean;
  onOpen?: () => void;
  attachment?: {
    id?: string | null;
    type: string;
    mediationId?: string;
    ids?: { id: string }[];
    costCenter?: string;
  };
}

const CustomTable = forwardRef<HTMLDivElement | null, CustomTableProps>(
  (
    {
      data,
      labels,
      isMediation,
      title,
      actionText,
      ariaText,
      total,
      loading,
      error,
      onOpen,
      attachment,
    },
    ref,
  ) => {
    const { handleToast } = useCustomToast();
    const [downloading, setDownloadLoading] = useState<boolean>(false);

    const [sort, setSort] = useState({
      row: '',
      direction: '',
      isNumber: false,
    });

    const sortedData = useMemo(() => {
      if (sort.row) {
        const arrayForSort = [...data];
        return arrayForSort.sort((a: any, b: any) => {
          if (!sort.isNumber)
            return sort.direction === 'desc'
              ? a[sort.row].localeCompare(b[sort.row])
              : b[sort.row].localeCompare(a[sort.row]);
          return sort.direction === 'desc'
            ? a[sort.row] - b[sort.row]
            : b[sort.row] - a[sort.row];
        });
      }

      return data;
    }, [sort, data]);

    const handleDowload = useCallback(async () => {
      {
        setDownloadLoading(true);

        try {
          if (attachment?.type === 'Boleto' && attachment.ids?.length)
            await Promise.all(
              attachment.ids.map(async item => {
                await downloadFile(
                  'financial',
                  attachment.type,
                  item.id,
                  attachment.mediationId,
                  attachment.costCenter,
                );
              }),
            );

          if (attachment?.id && attachment.mediationId) {
            await downloadFile(
              'financial',
              attachment.type,
              attachment.id,
              attachment.mediationId,
              attachment.costCenter,
            );
            handleToast({
              step: 5,
              action: 'O download do arquivo foi concluído',
            });
          }
        } catch (error) {
          handleToast({
            step: 4,
            action: 'fazer o download do arquivo',
          });
        }
        setDownloadLoading(false);
      }
    }, [attachment]);

    return (
      <Box ref={ref}>
        <Card
          py="0px"
          px="0px"
          minH="0px"
          borderRadius="16px"
          justifyContent="start"
          w={{ base: '100%', lg: '833px' }}
        >
          {loading ? (
            <Flex overflow="auto" position="relative" w="100%">
              <TableSkele labels={labels} />
            </Flex>
          ) : !error && !loading && data ? (
            <>
              <Flex
                px="16px"
                mt="22px"
                mb="24px"
                justifyContent="space-between"
                alignItems="center"
                flexDir={{ base: 'column', sm: 'row' }}
                gap={4}
              >
                <Text fontWeight="semibold" textColor="text.100">
                  {title}
                </Text>
                <Flex gap={4}>
                  <Button
                    isDisabled={
                      attachment?.ids ? !attachment.ids.length : !attachment?.id
                    }
                    _hover={{ bg: 'mutable.200' }}
                    isLoading={downloading}
                    onClick={handleDowload}
                    aria-label={ariaText}
                    leftIcon={
                      attachment?.id ? (
                        <HiDocumentSearch color="white" />
                      ) : (
                        <></>
                      )
                    }
                  >
                    {attachment?.id || attachment?.ids?.[0]
                      ? actionText
                      : 'Arquivo indisponível'}
                    {}
                  </Button>
                  {onOpen && (
                    <Button
                      onClick={onOpen}
                      _hover={{ bg: 'grey.300' }}
                      border="1px solid"
                      fontWeight="400"
                      variant="outline"
                    >
                      Entrar em contato
                    </Button>
                  )}
                </Flex>
              </Flex>

              <TableContainer>
                <Table>
                  <Thead bg="grey.400">
                    <Tr>
                      {labels?.map((item: any) => (
                        <Th
                          key={item.title}
                          fontSize="12px"
                          fontWeight={400}
                          textTransform="none"
                          textColor="text.100"
                          onClick={() =>
                            item.canSort &&
                            setSort(state => ({
                              direction:
                                item.content !== state.row
                                  ? 'desc'
                                  : state.direction === 'desc'
                                  ? 'asc'
                                  : 'desc',
                              isNumber: item.isNumber,
                              row: item.content,
                            }))
                          }
                        >
                          {item.title}
                          {sort?.row === item.content ? (
                            sort.direction === 'desc' ? (
                              <TriangleDownIcon
                                ml="8px"
                                aria-label="ordenado descendente"
                              />
                            ) : (
                              <TriangleUpIcon
                                ml="8px"
                                aria-label="ordenado ascendente"
                              />
                            )
                          ) : null}
                        </Th>
                      ))}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {sortedData?.map((item: any, index: number) => (
                      <Tr key={index}>
                        {Object.keys(item).map(key => (
                          <Td
                            key={index + item[key]}
                            py="8px"
                            borderColor={
                              isMediation ? 'grey.200' : 'transparent'
                            }
                            fontWeight={400}
                            textTransform="none"
                            textColor="text.100"
                            fontSize="12px"
                          >
                            {item[key] || '-'}
                          </Td>
                        ))}
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>

              {isMediation && (
                <Flex
                  w="100%"
                  px="16px"
                  my="8px"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Text fontSize="12px" fontWeight="semibold" textColor="black">
                    Valor a faturar
                  </Text>
                  <Text fontSize="12px" fontWeight="semibold" textColor="black">
                    {total || '-'}
                  </Text>
                </Flex>
              )}
            </>
          ) : (
            <Center my="26px" flexDirection="column">
              <Icon
                borderRadius="100px"
                padding="16px"
                bg="grey.200"
                w="80px"
                h="80px"
                as={HiOutlineExclamation}
                color="grey.600"
              />
              <Text mt="8px" fontWeight="medium" color="black" fontSize="18px">
                {title}
              </Text>
              <Text mt="4px" color="text.200" fontSize="12px">
                Não disponível no momento
              </Text>
            </Center>
          )}
        </Card>
      </Box>
    );
  },
);

CustomTable.displayName = 'CustomTable';

export { CustomTable };
